import { assignCompanyPlanBody } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@planuze/ui';
import { useEffect, useState } from 'react';
import { useCompaniesContent } from '../../content/sections/companies.js';
import { useAssignCompanyPlan } from '../../hooks/use-companies.js';
import { CapabilitiesEditor } from '../plans/capabilities-editor.js';
import { CurrencyInput } from '../plans/currency-input.js';
import { companyErrorMessage } from './company-error.js';

type Caps = Record<string, boolean | number>;
type Interval = 'month' | 'year';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly companyId: string;
  readonly companyName: string;
}

/* Form de atribuição do plano CUSTOM da org (C6, ADR-0422): o admin DEFINE o plano da
   org (tier + capabilities próprias). Cria Product + Price no Stripe → `DialogContent
   locked` durante a mutation (irreversível, ADR-0216). O owner conclui a assinatura
   depois (paga, C3). Sem orgId no body — a org vem da URL. */
export function AssignPlanModal({ open, onClose, companyId, companyName }: Props) {
  const copy = useCompaniesContent();
  const assign = useAssignCompanyPlan(companyId);

  const [tier, setTier] = useState('enterprise');
  const [planKey, setPlanKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [priceCents, setPriceCents] = useState(0);
  const [interval, setIntervalValue] = useState<Interval>('month');
  const [capabilities, setCapabilities] = useState<Caps>({});

  useEffect(() => {
    if (!open) return;
    setTier('enterprise');
    setPlanKey('');
    setDisplayName('');
    setPriceCents(0);
    setIntervalValue('month');
    setCapabilities({});
  }, [open]);

  const input = {
    tier,
    planKey,
    displayName,
    priceCents,
    currency: 'usd',
    interval,
    rank: 0,
    capabilities,
  };
  const valid = assignCompanyPlanBody.safeParse(input).success;

  const submit = (): void => {
    assign.mutate(input, {
      onSuccess: () => {
        toast.success(copy.assignPlanModal.success);
        onClose();
      },
      onError: (err) => toast.error(companyErrorMessage(err, copy.errors)),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !assign.isPending) onClose();
      }}
    >
      <DialogContent locked={assign.isPending}>
        <DialogHeader>
          <DialogTitle>{copy.assignPlanModal.title(companyName)}</DialogTitle>
          <DialogDescription>{copy.assignPlanModal.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ap-tier">{copy.assignPlanModal.tierLabel}</Label>
              <Input
                id="ap-tier"
                value={tier}
                placeholder="enterprise"
                onChange={(e) => setTier(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ap-interval">{copy.assignPlanModal.intervalLabel}</Label>
              <Select value={interval} onValueChange={(v) => setIntervalValue(v as Interval)}>
                <SelectTrigger id="ap-interval" aria-label={copy.assignPlanModal.intervalLabel}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">{copy.assignPlanModal.intervalMonthly}</SelectItem>
                  <SelectItem value="year">{copy.assignPlanModal.intervalYearly}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ap-key">{copy.assignPlanModal.lookupKeyLabel}</Label>
            <Input
              id="ap-key"
              value={planKey}
              placeholder="planuze_acme_enterprise"
              onChange={(e) => setPlanKey(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ap-name">{copy.assignPlanModal.displayNameLabel}</Label>
            <Input
              id="ap-name"
              value={displayName}
              placeholder="Acme Enterprise"
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ap-price">{copy.assignPlanModal.priceLabel}</Label>
            <CurrencyInput
              id="ap-price"
              value={priceCents}
              currency="USD"
              onChange={setPriceCents}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{copy.assignPlanModal.capabilitiesLabel}</Label>
            <CapabilitiesEditor value={capabilities} onChange={setCapabilities} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={assign.isPending}>
            {copy.assignPlanModal.cancel}
          </Button>
          <Button loading={assign.isPending} disabled={!valid} onClick={submit}>
            {copy.assignPlanModal.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
